import { z } from 'zod';

export const ExecutionActionSchema = z.object({
  kind: z.enum(['connection', 'archive']),
  connection_id: z.string().uuid().nullable(),
  operation: z.string().trim().min(1).max(100),
  type: z.string().trim().max(160).nullable(),
  ref: z.string().trim().max(300).nullable(),
  values: z.array(z.object({ name: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/), value: z.string().max(4000).nullable() })).max(40),
});
export type ExecutionAction = z.infer<typeof ExecutionActionSchema>;

export const ExecutionStepSchema = z.object({
  step_index: z.number().int().min(0).max(100),
  mode: z.enum(['person', 'assisted', 'system']),
  system_id: z.string().uuid().nullable().default(null),
  level: z.enum(['trial', 'propose', 'within_limits']).default('trial'),
  /** Per programmi e automazioni deterministiche: {{dato}}, {{sede}} e {{compito}} sono variabili del compito. */
  action: ExecutionActionSchema.nullable().default(null),
});
export const ProcedureExecutionSettingsSchema = z.object({
  stopped: z.boolean().default(false),
  steps: z.array(ExecutionStepSchema).max(101).refine((rows) => new Set(rows.map((s) => s.step_index)).size === rows.length, 'Un passo compare più volte'),
  max_turns: z.number().int().min(1).max(12).default(4),
  max_actions: z.number().int().min(1).max(10).default(3),
  max_cost_usd: z.number().positive().max(100).default(0.25),
  monthly_budget_usd: z.number().positive().max(10000).default(10),
  max_amount: z.number().nonnegative().max(100000000).nullable().default(null),
  allowed_recipients: z.array(z.string().trim().toLowerCase().email()).max(100).default([]),
  separate_approval: z.boolean().default(false),
});
export type ProcedureExecutionSettings = z.infer<typeof ProcedureExecutionSettingsSchema>;
export interface ProcedureExecutionConfig extends ProcedureExecutionSettings { procedure_id: string; version: number }

export const ExecutionStartSchema = z.object({ step_index: z.number().int().min(0).max(100).optional() });
export const ExecutionStopSchema = z.object({ stopped: z.boolean(), system_id: z.string().uuid().optional() });
export const ExecutionTakeoverSchema = z.object({ note: z.string().trim().min(1).max(2000) });
export const ExecutionCompleteStepSchema = z.object({ evidence: z.string().trim().min(1).max(4000) });
export const EXECUTION_STATES = ['preparing', 'trial', 'proposed', 'running', 'completed', 'fallback', 'stopped', 'failed', 'uncertain'] as const;
export type ExecutionState = (typeof EXECUTION_STATES)[number];
export interface ExecutorRunDto {
  id: string; task_id: string; procedure_id: string; procedure: string; step_index: number; step: string;
  system_id: string; system: string; state: ExecutionState; level: 'trial' | 'propose' | 'within_limits';
  config_version: number; summary: string | null; actions: ExecutionAction[]; evidence: string | null;
  cost_usd: number; created_at: string; updated_at: string; approved_by: string | null;
  can_approve: boolean; can_takeover: boolean;
  events: Array<{ at: string; kind: string; actor: string; message: string }>;
}
export interface TaskExecutionState {
  configured: boolean;
  runs: ExecutorRunDto[];
  steps: Array<{ index: number; name: string; mode: 'person' | 'assisted' | 'system'; state: 'pending' | 'done'; evidence: string | null; can_complete: boolean }>;
}

/** Operazioni esplicite sul collegamento: gli indirizzi e i segreti non arrivano mai al modello. */
export const ExecutorConnectionOperationSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/),
  label: z.string().trim().min(1).max(120),
  kind: z.enum(['read', 'write']),
  transport: z.enum(['rest', 'mcp']),
  method: z.enum(['GET', 'POST', 'PUT', 'PATCH']).default('GET'),
  path: z.string().min(1).max(500).default('/'),
  tool: z.string().min(1).max(100).optional(),
  inputs: z.array(z.object({ name: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/), required: z.boolean().default(true), type: z.enum(['string', 'number', 'boolean', 'json']).default('string') })).max(40).default([]),
  body: z.record(z.string().max(4000)).default({}),
  query: z.record(z.string().max(1000)).default({}),
  /** Chiave di un'operazione read nella stessa lista e campi che devono coincidere con gli input. */
  verify: z.object({ operation: z.string().min(1).max(100), matches: z.array(z.object({ field: z.string().min(1).max(200), input: z.string().min(1).max(63) })).min(1).max(10) }).nullable().default(null),
  idempotency_header: z.string().regex(/^[A-Za-z][A-Za-z0-9-]{0,79}$/).nullable().default('Idempotency-Key'),
}).superRefine((o, ctx) => {
  if (o.transport === 'mcp' && !o.tool) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Indica lo strumento MCP', path: ['tool'] });
  if (o.kind === 'write' && !o.verify) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Una scrittura richiede una rilettura di verifica', path: ['verify'] });
  if (o.kind === 'write' && o.transport === 'rest' && o.method === 'GET') ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Una scrittura non usa GET', path: ['method'] });
});
export type ExecutorConnectionOperation = z.infer<typeof ExecutorConnectionOperationSchema>;
export const ExecutorConnectionOperationsSchema = z.object({ operations: z.array(ExecutorConnectionOperationSchema).max(40).refine((ops) => new Set(ops.map((o) => o.key)).size === ops.length, 'Un’operazione compare più volte') });

export const SystemMcpConnectionSchema = z.object({
  software_id: z.string().uuid(),
  label: z.string().trim().min(1).max(120),
  url: z.string().url().max(500),
  auth: z.enum(['none', 'bearer', 'api_key']),
  header: z.string().regex(/^[A-Za-z][A-Za-z0-9-]{0,79}$/).default('X-API-Key'),
  secret: z.string().min(1).max(4000).optional(),
  operations: z.array(ExecutorConnectionOperationSchema).min(1).max(40),
}).superRefine((value, ctx) => {
  if (value.auth !== 'none' && !value.secret) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Indica il segreto dell’utenza', path: ['secret'] });
  if (value.operations.some((o) => o.transport !== 'mcp')) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Usa strumenti MCP per questo collegamento', path: ['operations'] });
});
export type SystemMcpConnectionInput = z.infer<typeof SystemMcpConnectionSchema>;
